import { useState, useMemo } from 'react'
import { Link, useForm, router } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Users, Building2, Server, GitBranch, Activity, Plus, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

interface User {
  id: string
  name: string
  email: string
  created_at: string
}

interface Stats {
  users_count: number
  clients_count: number
  instances_count: number
  workflows_count: number
  executions_count: number
}

interface Props {
  users: User[]
  current_user_id: string
  stats: Stats
}

type UserSortKey = 'name' | 'email' | 'created_at'
type SortDirection = 'asc' | 'desc'

function AddUserDialog() {
  const [open, setOpen] = useState(false)
  const { data, setData, post, processing, errors, reset } = useForm({
    user: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/users', {
      onSuccess: () => {
        setOpen(false)
        reset()
      },
    })
  }

  const formData = data.user
  const setFormData = (key: keyof typeof formData, value: string) => {
    setData('user', { ...formData, [key]: value })
  }

  const formErrors = errors as Record<string, string>

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create a new user account.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData('name', e.target.value)}
                placeholder="John Doe"
              />
              {formErrors['user.name'] && <p className="text-sm text-destructive">{formErrors['user.name']}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData('email', e.target.value)}
                placeholder="john@example.com"
              />
              {formErrors['user.email'] && <p className="text-sm text-destructive">{formErrors['user.email']}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData('password', e.target.value)}
                placeholder="Minimum 8 characters"
              />
              {formErrors['user.password'] && <p className="text-sm text-destructive">{formErrors['user.password']}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password_confirmation">Confirm Password</Label>
              <Input
                id="password_confirmation"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => setFormData('password_confirmation', e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditUserDialog({ user }: { user: User }) {
  const [open, setOpen] = useState(false)
  const { data, setData, patch, processing, errors } = useForm({
    user: {
      name: user.name,
      email: user.email,
      password: '',
      password_confirmation: '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/users/${user.id}`, {
      onSuccess: () => setOpen(false),
    })
  }

  const formData = data.user
  const setFormData = (key: keyof typeof formData, value: string) => {
    setData('user', { ...formData, [key]: value })
  }

  const formErrors = errors as Record<string, string>

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user account details. Leave password blank to keep current.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData('name', e.target.value)}
              />
              {formErrors['user.name'] && <p className="text-sm text-destructive">{formErrors['user.name']}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData('email', e.target.value)}
              />
              {formErrors['user.email'] && <p className="text-sm text-destructive">{formErrors['user.email']}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-password">New Password (optional)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData('password', e.target.value)}
                placeholder="Leave blank to keep current"
              />
              {formErrors['user.password'] && <p className="text-sm text-destructive">{formErrors['user.password']}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-password_confirmation">Confirm New Password</Label>
              <Input
                id="edit-password_confirmation"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => setFormData('password_confirmation', e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DeleteUserButton({ user, isCurrentUser }: { user: User; isCurrentUser: boolean }) {
  const handleDelete = () => {
    router.delete(`/users/${user.id}`)
  }

  if (isCurrentUser) {
    return (
      <Button variant="ghost" size="icon" disabled title="Cannot delete yourself">
        <Trash2 className="h-4 w-4 text-muted-foreground" />
      </Button>
    )
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {user.name}? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default function SettingsIndex({ users, current_user_id, stats }: Props) {
  const [sortKey, setSortKey] = useState<UserSortKey | null>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Sort users
  const sortedUsers = useMemo(() => {
    if (!sortKey) return users
    return [...users].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null

      if (sortKey === 'created_at') {
        aVal = a.created_at ? new Date(a.created_at).getTime() : null
        bVal = b.created_at ? new Date(b.created_at).getTime() : null
      } else {
        aVal = a[sortKey]
        bVal = b[sortKey]
      }

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [users, sortKey, sortDirection])

  const handleSort = (key: UserSortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ column }: { column: UserSortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  return (
    <Layout>
      <PageHead title="Settings" description="System settings and user management." />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            System settings and user management.
          </p>
        </div>

        {/* System Statistics */}
        <Card>
          <CardHeader>
            <CardTitle>System Statistics</CardTitle>
            <CardDescription>
              Overview of data in the system.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <a href="#users" className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-muted transition-colors">
                <Users className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.users_count}</p>
                  <p className="text-sm text-muted-foreground">Users</p>
                </div>
              </a>
              <Link href="/clients" className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-muted transition-colors">
                <Building2 className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.clients_count}</p>
                  <p className="text-sm text-muted-foreground">Clients</p>
                </div>
              </Link>
              <Link href="/n8n_instances" className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-muted transition-colors">
                <Server className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.instances_count}</p>
                  <p className="text-sm text-muted-foreground">Instances</p>
                </div>
              </Link>
              <Link href="/workflows" className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-muted transition-colors">
                <GitBranch className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.workflows_count}</p>
                  <p className="text-sm text-muted-foreground">Workflows</p>
                </div>
              </Link>
              <Link href="/" className="flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-muted transition-colors">
                <Activity className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.executions_count}</p>
                  <p className="text-sm text-muted-foreground">Executions</p>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Users Management */}
        <Card id="users">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  Manage user accounts.
                </CardDescription>
              </div>
              <AddUserDialog />
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button onClick={() => handleSort('name')} className="flex items-center font-medium hover:text-foreground">
                      Name <SortIcon column="name" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button onClick={() => handleSort('email')} className="flex items-center font-medium hover:text-foreground">
                      Email <SortIcon column="email" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button onClick={() => handleSort('created_at')} className="flex items-center font-medium hover:text-foreground">
                      Created <SortIcon column="created_at" />
                    </button>
                  </TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.name}
                      {user.id === current_user_id && (
                        <Badge variant="secondary" className="ml-2">You</Badge>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <EditUserDialog user={user} />
                        <DeleteUserButton user={user} isCurrentUser={user.id === current_user_id} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>About</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Osher n8n Fleet Dashboard - Internal dashboard for tracking usage, errors, and estimated time saved across multiple self-hosted n8n instances.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Version 1.0.0
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
